import React, { Dispatch, SetStateAction } from 'react';
import CardLayout from '../../../../shared/components/card/CardLayout';
import { CustomImageHandler } from '../../../../shared/components/CustomImageHandler';
import { formatDate } from '../../../../shared/functions/Functions';
import { Button, Dropdown, Image } from 'react-bootstrap';
import { Campaign, IInvoices } from '../../../../model/campaign/Campaign';
import { useTranslation } from 'react-i18next';
import { OfferStatusEnum } from '../../../../enum/OfferStatusEnum';
import { priceSplitter } from '../../../../utils/helpers/priceSplitter';
import { FaChevronDown, FaChevronUp, FaDownload, FaEllipsisV } from 'react-icons/fa';
import { Fancybox } from '../../../../shared/components/Fancybox';
import { FaRegTrashCan } from 'react-icons/fa6';
import { useLocation } from 'react-router-dom';
import PaymentInfoBlock from '../../../../shared/components/PaymentInfoBlock';
import { PaymentTermsEnum } from '../../../../enum/PaymenTermsEnum';
import { OfferIcon, OpenIcon } from '../../../../shared/Icons';

interface IInvoiceMainCardProps {
    selectedCampaign?: Campaign;
    openModalAction: (isContractModal: boolean, isLongTermOffer?: boolean, campaignId?: number, title?: string) => void;
    openUploadModal: (
        invoiceId: number,
        sequenceNumber: number,
        file?: string,
        rejectReason?: string,
        isLongTermOffer?: boolean,
        selectedCampaignId?: number,
        numberInvoices?: number,
        campaignTitle?: string
    ) => void;
    openDeleteModal: Dispatch<SetStateAction<{ id: number; show: boolean; title: string }>>;
    downloadInvoice: (id: number) => void;
    expended?: boolean;
}

const InvoiceMainCard = ({
    selectedCampaign,
    openModalAction,
    openUploadModal,
    openDeleteModal,
    downloadInvoice,
    expended,
}: IInvoiceMainCardProps) => {
    const { t } = useTranslation();
    const location = useLocation();
    const offer = selectedCampaign?.offers?.[0];
    const invoice: IInvoices | undefined = Array.isArray(selectedCampaign?.invoices)
        ? selectedCampaign?.invoices?.[0]
        : selectedCampaign?.invoices;
    const isOfferCompleted = offer?.status === OfferStatusEnum.COMPLETED;
    const isLongTermCampaign = selectedCampaign?.isLongTerm;
    const isInvoiceRejected = invoice?.isRejected;
    const isInvoiceVerified = invoice?.isVerified;
    const isPaidInvoice = invoice?.isPaid;
    const isMissingInvoice = !invoice;
    const isBeingVerified = !!invoice && !isInvoiceRejected && !isInvoiceVerified && !isPaidInvoice;

    const isAccordionVisible =
        isLongTermCampaign &&
        !!selectedCampaign?.offers?.[0]?.invoiceSchedules?.length &&
        location?.pathname?.includes('/invoices/archive');

    const showDropdownAction =
        !isLongTermCampaign &&
        (isInvoiceVerified ||
            isPaidInvoice ||
            isInvoiceRejected ||
            !isOfferCompleted ||
            isBeingVerified ||
            isMissingInvoice);
    const mainActions = () => {
        if (isLongTermCampaign) {
            return (
                <Button
                    disabled={!openModalAction}
                    onClick={event => {
                        event.stopPropagation();
                        openModalAction(true, offer?.isLongTerm, selectedCampaign?.id, selectedCampaign?.title);
                    }}
                >
                    {t('influencer.invoice.contract')}
                </Button>
            );
        }

        if (!isOfferCompleted) {
            return (
                <div className="d-flex align-items-center h-100">
                    <h6 className="mb-0 text-muted text-uppercase">
                        {t('influencer.invoice.reporting_not_completed')}
                    </h6>
                </div>
            );
        }

        if (isMissingInvoice) {
            return (
                <Button variant="primary" onClick={() => openUploadModal(selectedCampaign?.id ?? 0, 0)}>
                    {t('general.button.upload')}
                </Button>
            );
        }

        if (isInvoiceVerified || isBeingVerified || isPaidInvoice) {
            return (
                <div className="d-inline-block">
                    <Fancybox>
                        <div
                            data-fancybox="content-gallery"
                            className="cursor-pointer"
                            data-src={invoice?.path ?? '/icons/filePlaceholder.png'}
                        >
                            <Button variant="outline-primary">{t('general.button.open')}</Button>
                        </div>
                    </Fancybox>
                </div>
            );
        }
        if (isInvoiceRejected) {
            return (
                <Button
                    variant="primary"
                    onClick={() => openUploadModal(invoice?.id ?? 0, 0, invoice?.path, invoice?.rejectReason)}
                >
                    {t('general.button.replace')}
                </Button>
            );
        }
    };

    const getInvoiceStatusImage = () =>
        isPaidInvoice
            ? 'paid.svg'
            : isInvoiceVerified
            ? 'approved.svg'
            : isInvoiceRejected
            ? 'rejected.svg'
            : isBeingVerified
            ? 'being-verified-icon.svg'
            : 'pending.svg';

    return (
        <CardLayout classBody="contract-list-card px-3 py-0">
            <div className="image-wrapper">
                <CustomImageHandler
                    thumbnailPath={selectedCampaign?.campaignImages?.[0]?.thumbnail}
                    photoPath={selectedCampaign?.campaignImages?.[0]?.thumbnail}
                    altTag="Campaign Image"
                />
                {(selectedCampaign?.isLongTerm || offer?.isLongTerm) && (
                    <span className={`status-badge ${selectedCampaign?.isLongTerm ? 'long' : 'linked'}`}>
                        {t(
                            `brand.campaign.briefing.overview_card.${
                                selectedCampaign?.isLongTerm ? 'long_term' : 'linked'
                            }`
                        )}
                    </span>
                )}
                {offer?.isAddonsOnly && (
                    <span className={`status-badge addons_only`}>
                        {t(`brand.campaign.briefing.overview_card.addons_only`)}
                    </span>
                )}
            </div>
            <div className="card-content pl-2 pl-md-3">
                <h5 className="mb-0 cursor-pointer">{selectedCampaign?.title ?? ''}</h5>
                <p className="mb-0 text-primary">{selectedCampaign?.company?.displayName ?? '-'}</p>
            </div>
            <div className="card-info">
                <div className="info-block">
                    {selectedCampaign?.isLongTerm ? (
                        <>
                            <div className="info-main">
                                {offer?.approvedOrPaidCount ?? 0}/{offer?.invoiceSchedules?.length ?? 0}
                            </div>
                            <div>{t('influencer.invoice.invoices')}</div>
                        </>
                    ) : (
                        <div className="ml-md-2 d-flex align-items-center">
                            <img
                                width={40}
                                height={40}
                                src={`/icons/approval-status/${getInvoiceStatusImage()}`}
                                alt="approval-status"
                            />
                            <div className="ml-1 text-start">
                                <div
                                    className={`info-main ${
                                        isInvoiceRejected
                                            ? 'text-danger'
                                            : isInvoiceVerified || isPaidInvoice
                                            ? 'text-primary'
                                            : 'text-muted'
                                    }`}
                                >
                                    {t(
                                        `influencer.invoice.statuses.${
                                            isInvoiceRejected
                                                ? 'rejected'
                                                : isPaidInvoice
                                                ? 'paid'
                                                : isInvoiceVerified
                                                ? 'approved'
                                                : isBeingVerified
                                                ? 'being_verified'
                                                : 'missing'
                                        }`
                                    )}
                                </div>
                                <div>{t('influencer.invoice.status')}</div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="info-block">
                    <PaymentInfoBlock
                        main={t(
                            `brand.campaign.briefing.step8.payment_terms_type.${
                                selectedCampaign?.paymentTerms === PaymentTermsEnum.OTHER
                                    ? 'custom'
                                    : selectedCampaign?.paymentTerms
                            }`
                        )}
                        label={t('brand.campaign.briefing.step8.payment_terms')}
                        showTooltip={selectedCampaign?.paymentTerms === PaymentTermsEnum.OTHER}
                        tooltipContent={selectedCampaign?.paymentTermsCustom ?? ''}
                        tooltipId="payment_tooltip"
                        mainClassName="info-main"
                    />
                </div>
                {selectedCampaign?.isLongTerm ? (
                    <div className="info-block">
                        <div className="info-main">
                            {!!offer?.nextInvoiceDate ? formatDate(offer?.nextInvoiceDate) : '-'}
                        </div>
                        <div>{t('influencer.invoice.next_invoice_date')}</div>
                    </div>
                ) : (
                    <div className="info-block">
                        <div className="info-main">{!!invoice?.date ? formatDate(invoice?.date) : '-'}</div>
                        <div>{t('influencer.invoice.upload_date')}</div>
                    </div>
                )}
                <div className="info-block">
                    {selectedCampaign?.isLongTerm ? (
                        <>
                            <div className="info-main euro-symbol">
                                {priceSplitter(offer?.totalAmountPaid ?? 0)} / {priceSplitter(offer?.price ?? 0)}
                            </div>
                            <div>{t('influencer.invoice.total_invoiced')}</div>
                        </>
                    ) : (
                        <>
                            <div className="info-main euro-symbol">{priceSplitter(offer?.price ?? 0)}</div>
                            <div>{t('influencer.invoice.invoiced')}</div>
                        </>
                    )}
                </div>
                <div className="d-flex align-items-center w-md-auto w-100 justify-content-end">
                    {showDropdownAction && (
                        <Dropdown className="btn-group position-static ml-2" drop="down">
                            <Dropdown.Toggle className="mr-2" variant="light" id="dropdown-basic" aria-label="Actions">
                                <FaEllipsisV className="text-secondary" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item
                                    className="d-flex align-items-center text-muted textHover"
                                    disabled={!openModalAction}
                                    onClick={() =>
                                        openModalAction(
                                            !!isLongTermCampaign,
                                            offer?.isLongTerm,
                                            selectedCampaign?.id,
                                            selectedCampaign?.title
                                        )
                                    }
                                >
                                    <OfferIcon width={14} height={14} className="mr-1" />
                                    {t('influencer.invoice.offer')}
                                </Dropdown.Item>
                                {isInvoiceRejected && (
                                    <Fancybox>
                                        <div
                                            data-fancybox="content-gallery"
                                            className="cursor-pointer"
                                            data-src={invoice?.path ?? '/icons/filePlaceholder.png'}
                                        >
                                            <Dropdown.Item
                                                target="_blank"
                                                rel="noreferrer"
                                                href={invoice?.path}
                                                className="d-flex align-items-center text-muted textHover"
                                            >
                                                <OpenIcon width={14} height={14} className="mr-1" />
                                                {t('general.button.open')}
                                            </Dropdown.Item>
                                        </div>
                                    </Fancybox>
                                )}
                                {(isBeingVerified || isInvoiceVerified || isPaidInvoice) && (
                                    <Dropdown.Item
                                        className="d-flex align-items-center text-muted"
                                        onClick={() => downloadInvoice(invoice?.id ?? 0)}
                                    >
                                        <FaDownload className="mr-1" />
                                        {t('general.download')}
                                    </Dropdown.Item>
                                )}
                                {isBeingVerified && (
                                    <Dropdown.Item
                                        onClick={() =>
                                            openDeleteModal(prevState => ({
                                                ...prevState,
                                                id: invoice?.id ?? 0,
                                                show: true,
                                                title: selectedCampaign?.title ?? '',
                                            }))
                                        }
                                        className="text-danger d-flex align-items-center"
                                    >
                                        <FaRegTrashCan className="mr-1" />
                                        {t('general.button.delete')}
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                    <div className="invoice-main-action d-inline-block">{mainActions()}</div>
                    {isAccordionVisible && (
                        <div className="ml-2">
                            {expended ? (
                                <Button variant="outline-secondary">
                                    <FaChevronUp />
                                </Button>
                            ) : (
                                <Button variant="outline-secondary">
                                    <FaChevronDown />
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </CardLayout>
    );
};

export default React.memo(InvoiceMainCard);
